import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import { LoadingButton } from '@mui/lab';
import {
  type Breakpoint,
  Button,
  Dialog as DialogCore,
  DialogActions as DialogActionsCore,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from '@mui/material';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export interface DialogActions {
  open: boolean;
  onConfirm?: () => void;
  onCancel?: () => void;
}

interface Props extends DialogActions {
  children: ReactNode;
  title: string;
  variant?: 'error' | 'warning' | 'info';
  confirmButtonText?: string;
  cancelButtonText?: string;
  isConfirmButtonDisabled?: boolean;
  isConfirmButtonSubmitting?: boolean;
  confirmButtonId?: string;
  cancelButtonId?: string;
  pagination?: ReactNode;
  fullWidth?: boolean;
  maxWidth?: Breakpoint | false;
}

const icons = {
  error: <ErrorIcon color="error" fontSize="large" />,
  warning: <WarningIcon color="warning" fontSize="large" />,
  info: <InfoIcon color="info" fontSize="large" />,
};

export const Dialog = ({
  children,
  onCancel,
  onConfirm,
  open,
  title,
  variant,
  confirmButtonText,
  cancelButtonText,
  isConfirmButtonDisabled = false,
  isConfirmButtonSubmitting = false,
  confirmButtonId,
  cancelButtonId,
  pagination,
  fullWidth,
  maxWidth,
}: Props) => {
  const { t } = useTranslation(['common']);

  return (
    <DialogCore
      onClose={onCancel}
      open={open}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      PaperProps={{
        sx: {
          width: '100%',
        },
      }}>
      <DialogTitle
        sx={{
          alignItems: 'center',
          display: 'flex',
        }}>
        {variant ? icons[variant] : null}
        <Typography color="inherit" sx={{ ml: 2 }} variant="inherit">
          {title}
        </Typography>
      </DialogTitle>
      <Divider />
      <DialogContent>{children}</DialogContent>
      {pagination}
      <DialogActionsCore>
        {onCancel ? (
          <Button color="primary" onClick={onCancel} variant="text" id={cancelButtonId}>
            {cancelButtonText ?? t('common:button.cancel')}
          </Button>
        ) : null}
        {onConfirm ? (
          <LoadingButton
            color="primary"
            onClick={onConfirm}
            loading={isConfirmButtonSubmitting}
            variant="contained"
            id={confirmButtonId}
            disabled={isConfirmButtonDisabled}>
            {confirmButtonText ?? t('common:button.save')}
          </LoadingButton>
        ) : null}
      </DialogActionsCore>
    </DialogCore>
  );
};
